import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import axios from '../../api/axios';

const QualcommAnalysis = () => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    fetchAnalysisData();
  }, []);

  const fetchAnalysisData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/QualcommAnalysis/completeAnalysis');
      setAnalysisData(response.data.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch Qualcomm/Arduino analysis data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Qualcomm/Arduino Analysis...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!analysisData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">No analysis data available</Alert>
      </Container>
    );
  }

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  const timelineData = analysisData.acquisitions?.map(acq => {
    const date = new Date(acq.date);
    return {
      date: date.getTime(),
      dateLabel: date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
      year: date.getFullYear(),
      value: acq.valueMillions,
      company: acq.company,
      category: acq.category,
      techImpact: acq.techImpactScore,
      clusterId: acq.clusterId
    };
  }).sort((a, b) => a.date - b.date) || [];

  const categoryData = analysisData.acquisitions?.reduce((acc, acq) => {
    const existing = acc.find(item => item.category === acq.category);
    if (existing) {
      existing.count += 1;
      existing.totalValue += acq.valueMillions;
    } else {
      acc.push({
        category: acq.category,
        count: 1,
        totalValue: acq.valueMillions
      });
    }
    return acc;
  }, []) || [];

  const innovationComponents = analysisData.innovationFactor?.components ?
    Object.entries(analysisData.innovationFactor.components).map(([key, value]) => ({
      component: key.replace(/([A-Z])/g, ' $1').trim(),
      value: value
    })) : [];

  const forecastData = analysisData.forecast?.forecastData?.map(item => ({
    year: item.year,
    actual: item.actualRevenue,
    predicted: item.predictedRevenue,
    lower: item.predictedRevenue - item.confidenceInterval,
    upper: item.predictedRevenue + item.confidenceInterval
  })) || [];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {}
      <Paper elevation={3} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
          Qualcomm/Arduino M&A Analysis
        </Typography>
        <Typography variant="subtitle1" sx={{ color: 'white', opacity: 0.9 }}>
          Edge Computing & AI Robotics Strategic Analysis (2024-2025)
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`${analysisData.acquisitions?.length || 0} Acquisitions`}
            sx={{ bgcolor: 'white', color: '#667eea', fontWeight: 'bold' }}
          />
          <Chip
            label={`Innovation Factor: ${analysisData.innovationFactor?.overallScore || 0}/10`}
            sx={{ bgcolor: 'white', color: '#764ba2', fontWeight: 'bold' }}
          />
          <Chip
            label="33M Developers (Arduino)"
            sx={{ bgcolor: 'white', color: '#667eea', fontWeight: 'bold' }}
          />
        </Box>
      </Paper>

      {}
      <Paper elevation={3} sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
          <Tab label="M&A Timeline" />
          <Tab label="Innovation Factor" />
          <Tab label="Revenue Forecast" />
          <Tab label="Key Findings" />
        </Tabs>
      </Paper>

      {}
      {currentTab === 0 && (
        <Grid container spacing={3}>
          {}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Acquisition Timeline (2011-2025)
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <ScatterChart margin={{ top: 20, right: 30, bottom: 60, left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="year"
                    name="Year"
                    type="number"
                    domain={['dataMin - 1', 'dataMax + 1']}
                    ticks={[2011, 2013, 2015, 2017, 2019, 2021, 2023, 2025]}
                    label={{ value: 'Year', position: 'insideBottom', offset: -10 }}
                  />
                  <YAxis
                    dataKey="value"
                    name="Value (M$)"
                    scale="log"
                    domain={[10, 10000]}
                    label={{ value: 'Acquisition Value (M$)', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => `$${value}M`}
                  />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <Box sx={{ bgcolor: 'white', p: 2, border: '1px solid #ccc', borderRadius: 1, boxShadow: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>{data.company}</Typography>
                            <Typography variant="body2">Date: {data.dateLabel}</Typography>
                            <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                              Value: ${data.value.toLocaleString()}M
                            </Typography>
                            <Typography variant="body2">Category: {data.category}</Typography>
                            <Typography variant="body2">Tech Impact: {data.techImpact}/10</Typography>
                            <Chip
                              label={data.clusterId === 1 ? 'Edge AI Cluster' : 'Strategic Outlier'}
                              size="small"
                              color={data.clusterId === 1 ? 'primary' : 'default'}
                              sx={{ mt: 1 }}
                            />
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <Scatter
                    name="Edge AI Cluster (2024-2025)"
                    data={timelineData.filter(d => d.clusterId === 1)}
                    fill="#667eea"
                    shape="circle"
                  />
                  <Scatter
                    name="Earlier Strategic Acquisitions"
                    data={timelineData.filter(d => d.clusterId === -1)}
                    fill="#82ca9d"
                    shape="diamond"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Acquisition Categories
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#8884d8" name="Count" />
                  <Bar dataKey="totalValue" fill="#82ca9d" name="Total Value (M$)" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Clustering Analysis
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Total Acquisitions
                      </Typography>
                      <Typography variant="h4">
                        {analysisData.clusteringResult?.totalPoints || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Edge AI Cluster
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {analysisData.clusteringResult?.clusteredPoints || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Silhouette Score
                      </Typography>
                      <Typography variant="h4">
                        {analysisData.clusteringResult?.silhouetteScore?.toFixed(2) || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Period
                      </Typography>
                      <Typography variant="h6">
                        2024-2025
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {currentTab === 1 && (
        <Grid container spacing={3}>
          {}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Overall Innovation Factor
              </Typography>
              <Box sx={{ position: 'relative', display: 'inline-flex', mt: 2 }}>
                <CircularProgress
                  variant="determinate"
                  value={(analysisData.innovationFactor?.overallScore || 0) * 10}
                  size={150}
                  thickness={5}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}
                >
                  <Typography variant="h3" component="div" color="primary">
                    {analysisData.innovationFactor?.overallScore?.toFixed(1) || 0}
                  </Typography>
                  <Typography variant="caption" component="div">
                    /10
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ mt: 2 }}>
                {analysisData.innovationFactor?.interpretation}
              </Typography>
            </Paper>
          </Grid>

          {}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Innovation Components
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={innovationComponents} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 10]} />
                  <YAxis dataKey="component" type="category" width={150} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Developer Ecosystem & Strategic Advantages
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                    Key Platforms:
                  </Typography>
                  {analysisData.ecosystemAnalysis?.keyPlatforms?.map((platform, index) => (
                    <Chip
                      key={index}
                      label={platform}
                      sx={{ m: 0.5 }}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                    Strategic Advantages:
                  </Typography>
                  {analysisData.ecosystemAnalysis?.strategicAdvantages?.map((advantage, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                      • {advantage}
                    </Typography>
                  ))}
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Market Position:</strong> {analysisData.ecosystemAnalysis?.marketPosition}
                </Typography>
              </Alert>
            </Paper>
          </Grid>
        </Grid>
      )}

      {currentTab === 2 && (
        <Grid container spacing={3}>
          {}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Revenue Forecast (2020-2030) - with Edge AI Growth
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                <strong>Algorithm:</strong> SSA (Singular Spectrum Analysis) with ML.NET
                <br />
                <strong>Parameters:</strong> 95% Confidence Level, Window Size: {analysisData.forecast?.historicalYears ? Math.floor(analysisData.forecast.historicalYears.length / 4) : 'N/A'}, Horizon: 5 years
                <br />
                <strong>Method:</strong> Time series decomposition into trend, seasonality, and noise components
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={forecastData} margin={{ top: 20, right: 30, bottom: 20, left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="year"
                    label={{ value: 'Year', position: 'insideBottom', offset: -10 }}
                  />
                  <YAxis
                    label={{ value: 'Revenue (Million $)', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}B`}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      if (value === null || value === undefined) return ['N/A', name];
                      return [`$${value.toLocaleString()}M`, name];
                    }}
                    labelFormatter={(label) => `Year: ${label}`}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <Area
                    type="monotone"
                    dataKey="upper"
                    stroke="none"
                    fill="#82ca9d"
                    fillOpacity={0.2}
                    name="Confidence Upper Bound"
                  />
                  <Area
                    type="monotone"
                    dataKey="lower"
                    stroke="none"
                    fill="#82ca9d"
                    fillOpacity={0.2}
                    name="Confidence Lower Bound"
                  />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    stroke="#8884d8"
                    strokeWidth={3}
                    dot={{ r: 5, fill: '#8884d8' }}
                    name="Actual Revenue"
                  />
                  <Line
                    type="monotone"
                    dataKey="predicted"
                    stroke="#ff7300"
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    dot={{ r: 5, fill: '#ff7300' }}
                    name="Predicted Revenue (SSA)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Key Growth Drivers
              </Typography>
              <Grid container spacing={2}>
                {analysisData.forecast?.keyDrivers?.map((driver, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body1">
                          • {driver}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {currentTab === 3 && (
        <Grid container spacing={3}>
          {}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Key Findings & Strategic Implications
              </Typography>
              {analysisData.keyFindings?.map((finding, index) => (
                <Alert key={index} severity="info" sx={{ mb: 2 }}>
                  {finding}
                </Alert>
              ))}
            </Paper>
          </Grid>

          {}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                DBSCAN Parameters Used
              </Typography>
              <Typography variant="body1">
                • Epsilon (eps): 180 days
              </Typography>
              <Typography variant="body1">
                • Minimum Points (minPts): 4 (adapted for N=13 using ln(N) guideline)
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                Parameters adapted from original EDA study (MinPts=10 for N=661) following
                Sander et al. (1998) guideline: MinPts ≥ ln(N) for smaller datasets.
              </Typography>
            </Paper>
          </Grid>

          {}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Analysis Metrics
              </Typography>
              <Typography variant="body1">
                • Method: SSA with Edge AI Growth Factor
              </Typography>
              <Typography variant="body1">
                • Model Accuracy: {(analysisData.forecast?.modelAccuracy * 100).toFixed(1)}%
              </Typography>
              <Typography variant="body1">
                • Silhouette Score: {analysisData.clusteringResult?.silhouetteScore?.toFixed(3)}
              </Typography>
              <Typography variant="body1">
                • Davies-Bouldin Index: {analysisData.clusteringResult?.daviesBouldinIndex?.toFixed(3)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default QualcommAnalysis;
